import { lazy } from 'react';
import { createBrowserRouter, Navigate } from 'react-router';

import { AppLayout } from '@/app/layouts/AppLayout';
import { RedirectIfAuthenticated } from '@/app/router/RedirectIfAuthenticated';
import { RequireAuth } from '@/app/router/RequireAuth';

/**
 * Маршруты приложения.
 *
 * Каждый экран загружается лениво: при старте в браузер уезжает только
 * календарь, остальные разделы подтягиваются по мере перехода. На бюджетном
 * Android это заметно сокращает время до первого экрана.
 */

/* eslint-disable react-refresh/only-export-components --
   Файл описывает конфигурацию маршрутов, а не компоненты. Ленивые обёртки
   здесь — часть этой конфигурации; выносить каждую в отдельный файл ради
   горячей перезагрузки значило бы размазать таблицу маршрутов по десятку мест. */

const CalendarPage = lazy(() =>
  import('@/pages/calendar/CalendarPage').then((m) => ({ default: m.CalendarPage })),
);
const TasksPage = lazy(() =>
  import('@/pages/tasks/TasksPage').then((m) => ({ default: m.TasksPage })),
);
const GoalsPage = lazy(() =>
  import('@/pages/goals/GoalsPage').then((m) => ({ default: m.GoalsPage })),
);
const HealthPage = lazy(() =>
  import('@/pages/health/HealthPage').then((m) => ({ default: m.HealthPage })),
);
const MorePage = lazy(() => import('@/pages/more/MorePage').then((m) => ({ default: m.MorePage })));
const ShoppingPage = lazy(() =>
  import('@/pages/shopping/ShoppingPage').then((m) => ({ default: m.ShoppingPage })),
);
const MediaPage = lazy(() =>
  import('@/pages/media/MediaPage').then((m) => ({ default: m.MediaPage })),
);
const ProfilePage = lazy(() =>
  import('@/pages/profile/ProfilePage').then((m) => ({ default: m.ProfilePage })),
);
const SphereSettingsPage = lazy(() =>
  import('@/pages/profile/SphereSettingsPage').then((m) => ({ default: m.SphereSettingsPage })),
);
const RoutineSettingsPage = lazy(() =>
  import('@/pages/profile/RoutineSettingsPage').then((m) => ({ default: m.RoutineSettingsPage })),
);
/*
  Съёмный раздел «Беременность». Он лежит целиком в `src/pages/pregnancy/` и
  ничего в приложении о себе не знает. Чтобы убрать его, достаточно удалить ту
  папку, эту строку и маршрут ниже — плюс пункт в «Ещё» (см. README раздела).
*/
const PregnancyPage = lazy(() =>
  import('@/pages/pregnancy/PregnancyPage').then((m) => ({ default: m.PregnancyPage })),
);
const AuthPage = lazy(() =>
  import('@/pages/auth/AuthPage').then((m) => ({ default: m.AuthPage })),
);

export const router = createBrowserRouter([
  {
    path: '/auth',
    element: (
      <RedirectIfAuthenticated>
        <AuthPage />
      </RedirectIfAuthenticated>
    ),
  },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Navigate to="/calendar" replace /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'tasks', element: <TasksPage /> },
      { path: 'goals', element: <GoalsPage /> },
      { path: 'health', element: <HealthPage /> },
      { path: 'more', element: <MorePage /> },
      { path: 'shopping', element: <ShoppingPage /> },
      { path: 'media', element: <MediaPage /> },
      // Съёмный раздел, см. комментарий выше.
      { path: 'pregnancy', element: <PregnancyPage /> },
      { path: 'profile', element: <ProfilePage /> },
      { path: 'profile/routine', element: <RoutineSettingsPage /> },
      { path: 'profile/spheres', element: <SphereSettingsPage /> },
      { path: '*', element: <Navigate to="/calendar" replace /> },
    ],
  },
]);
